import React, { useRef } from 'react';
import { SxProps } from '@mui/material';
import { json } from '@codemirror/lang-json';
import { openSearchPanel } from '@codemirror/search';
import { dracula } from '@uiw/codemirror-theme-dracula';
import CodeMirror, {
    Extension,
    ReactCodeMirrorRef,
} from '@uiw/react-codemirror';
import { CodeMirrorStyles } from '../../lib';
import ReadOnlyWrapper from '../read-only-wrapper/ReadOnlyWrapper';

interface CodeEditorProps {
    jsonData: any;
    maxHeight?: string;
    theme?: Extension;
    wrapperContainerStyles?: SxProps;
}
const ReadOnlyJsonEditor = ({
    jsonData,
    maxHeight,
    theme = dracula,
    wrapperContainerStyles,
}: CodeEditorProps): React.JSX.Element => {
    const codeMirrorRef = useRef<ReactCodeMirrorRef>(null);

    /**
     * This was a somewhat obscure issue to solve due to the lack of examples/documentation from CodeMirror
     * and how the codemirror view and states are created. The current solution was found after researching
     * the following examples:
     *
     * https://discuss.codemirror.net/t/how-to-programatically-open-the-search-panel/5443/3
     * https://github.com/uiwjs/react-codemirror/issues/314#issuecomment-1863873640
     */
    const editorRefCallback = (editor: ReactCodeMirrorRef): void => {
        if (
            !codeMirrorRef.current &&
            editor?.editor &&
            editor?.state &&
            editor?.view
        ) {
            // open the editor view search panel by default to allow the user search through the JSON code.
            openSearchPanel(editor.view);
        }
    };

    // The current jsonObject could potentially have any kind of shape, so we want to explicitly use any here.
    const sortObject = (obj: any): any => {
        if (typeof obj !== 'object' || obj === null) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(sortObject);
        }

        // sort top-level keys
        const sortedKeys = Object.keys(obj).sort((a: any, b: any) => {
            return a.toLowerCase().localeCompare(b.toLowerCase());
        });
        const sortedObj: any = {};

        // for every top-level sorted key, recursively call the sort function to sort inner elements.
        for (const key of sortedKeys) {
            sortedObj[key] = sortObject(obj[key]);
        }

        return sortedObj;
    };

    return (
        <ReadOnlyWrapper customTextContainerStyles={wrapperContainerStyles}>
            <CodeMirror
                maxHeight={maxHeight}
                ref={editorRefCallback}
                value={JSON.stringify(
                    jsonData,
                    (_key, value) => sortObject(value),
                    2
                )}
                extensions={[json(), CodeMirrorStyles]}
                theme={theme}
                readOnly
            />
        </ReadOnlyWrapper>
    );
};

export const MemoReadOnlyJsonEditor = React.memo(ReadOnlyJsonEditor);
